import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
    CreateBrandCategoryRequestBody,
    CreateLayoutFormatRequestBody,
    CreateLayoutReferenceRequestBody,
    UpdateBrandCategoryRequestBody,
    UpdateLayoutFormatRequestBody,
    UpdateLayoutReferenceRequestBody,
} from '@storefronts/common-types'
import { R } from '@/modules/api/actions'

export const LAYOUT_REFERENCES_QUERY_KEY = 'layout-references'
export const LAYOUT_FORMATS_QUERY_KEY = 'layout-formats'
export const BRAND_CATEGORIES_QUERY_KEY = 'brand-categories'

const LAYOUT_SETUP_QUERY_KEYS = [
    [LAYOUT_REFERENCES_QUERY_KEY],
    [LAYOUT_FORMATS_QUERY_KEY],
    [BRAND_CATEGORIES_QUERY_KEY],
] as const

function invalidateLayoutSetup(queryClient: ReturnType<typeof useQueryClient>) {
    for (const queryKey of LAYOUT_SETUP_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey })
    }
}

export function useLayoutReferencesQuery(enabled = true) {
    return useQuery({
        queryKey: [LAYOUT_REFERENCES_QUERY_KEY],
        queryFn: async () =>
            (await R.getAllLayoutReferences()).layoutReferences,
        enabled,
    })
}

export function useLayoutFormatsQuery(enabled = true) {
    return useQuery({
        queryKey: [LAYOUT_FORMATS_QUERY_KEY],
        queryFn: async () => (await R.getAllLayoutFormats()).layoutFormats,
        enabled,
    })
}

export function useBrandCategoriesQuery(enabled = true) {
    return useQuery({
        queryKey: [BRAND_CATEGORIES_QUERY_KEY],
        queryFn: async () => (await R.getAllBrandCategories()).brandCategories,
        enabled,
    })
}

export function useCreateLayoutReferenceMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (body: CreateLayoutReferenceRequestBody) =>
            R.createLayoutReference(body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useUpdateLayoutReferenceMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateLayoutReferenceRequestBody
        }) => R.updateLayoutReference(id, body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useDeleteLayoutReferenceMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: number) => R.deleteLayoutReference(id),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useCreateLayoutFormatMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (body: CreateLayoutFormatRequestBody) =>
            R.createLayoutFormat(body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useUpdateLayoutFormatMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateLayoutFormatRequestBody
        }) => R.updateLayoutFormat(id, body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useDeleteLayoutFormatMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: number) => R.deleteLayoutFormat(id),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useCreateBrandCategoryMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (body: CreateBrandCategoryRequestBody) =>
            R.createBrandCategory(body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useUpdateBrandCategoryMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateBrandCategoryRequestBody
        }) => R.updateBrandCategory(id, body),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}

export function useDeleteBrandCategoryMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: number) => R.deleteBrandCategory(id),
        onSuccess: () => invalidateLayoutSetup(queryClient),
    })
}
